import {
  DietMealProps,
  CookbookMealProps,
  FavMealProps,
  TrackerMealProps,
  TrackerAddMealProps,
  DescribeMealProps,
  Ingredient,
  IngredientMeasurement,
} from '../types';

// Helper function to sum ingredients with the same name
const sumIngredients = (ingredients: Ingredient[], portions: number): Ingredient[] => {
  const ingredientMap = new Map<string, Ingredient>();

  ingredients.forEach((ingredient) => {
    if (ingredientMap.has(ingredient.name)) {
      // If ingredient already exists, combine measurements
      const existing = ingredientMap.get(ingredient.name);
      if (!existing) return;

      const combinedMeasurements = [
        ...(existing.measurements || []),
        ...(ingredient.measurements || []),
      ];

      // Sum measurements with the same unit
      const measurementMap = new Map<string, IngredientMeasurement>();

      combinedMeasurements.forEach((measurement) => {
        const key = measurement.unit;
        if (measurementMap.has(key)) {
          const existingMeasurement = measurementMap.get(key);
          if (!existingMeasurement) return;
          measurementMap.set(key, {
            ...existingMeasurement,
            amount: existingMeasurement.amount + measurement.amount,
          });
        } else {
          measurementMap.set(key, { ...measurement });
        }
      });

      ingredientMap.set(ingredient.name, {
        ...existing,
        measurements: Array.from(measurementMap.values()),
      });
    } else {
      // Add new ingredient
      ingredientMap.set(ingredient.name, {
        ...ingredient,
        measurements: ingredient.measurements ? [...ingredient.measurements] : [],
      });
    }
  });

  return Array.from(ingredientMap.values()).map((ingredient, index) => ({
    ...ingredient,
    id: `ingredient_${index + 1}`,
    measurements: ingredient.measurements?.map((measurement) => ({
      ...measurement,
      amount: Math.round((measurement.amount / portions) * 100) / 100,
    })),
    kcal: Math.round((ingredient.kcal / portions) * 100) / 100,
    macro: ingredient.macro
      ? {
          protein: {
            grams: Math.round((ingredient.macro.protein.grams / portions) * 100) / 100,
          },
          fat: {
            grams: Math.round((ingredient.macro.fat.grams / portions) * 100) / 100,
          },
          carbohydrates: {
            grams: Math.round((ingredient.macro.carbohydrates.grams / portions) * 100) / 100,
          },
        }
      : undefined,
  }));
};

// Helper functions to normalize different data structures
export const normalizeDietMeal = (props: DietMealProps): TrackerAddMealProps => {
  // Extract all ingredients from all dishes
  const allIngredients = props.dishes.flatMap((dish) => dish.ingredients);

  // Sum ingredients with the same name
  const summedIngredients = sumIngredients(allIngredients, 1); // 1 is number of portions

  return {
    mode: 'backward-navigation',
    mealData: {
      id: props.id,
      name: props.name,
      kcal: props.kcal,
      macro: props.macro,
      preparationImageUrl: props.preparationImageUrl,
      mealType: props.mealType?.type,
      sourceType: 'diet',
      ingredients: summedIngredients,
      portions: 1,
    },
    loading: props.loading,
    addMeal: props.addMeal,
  };
};

export const normalizeCookbookMeal = (props: CookbookMealProps): TrackerAddMealProps => {
  // Extract all ingredients from all dishes
  const allIngredients = props.dishes.flatMap((dish) => dish.ingredients);

  // Sum ingredients with the same name
  const summedIngredients = sumIngredients(allIngredients, props.portions);

  return {
    mode: 'backward-navigation',
    mealData: {
      id: props.id,
      name: props.name,
      kcal: props.nutrition.singleServing.kcal,
      macro: {
        protein: {
          grams: props.nutrition.singleServing.protein.grams,
        },
        fat: {
          grams: props.nutrition.singleServing.fat.grams,
        },
        carbohydrates: {
          grams: props.nutrition.singleServing.carbohydrates.grams,
        },
      },
      preparationImageUrl: props.preparationImageUrl,
      sourceType: 'cookbook',
      ingredients: summedIngredients,
      portions: 1,
    },
    loading: props.loading,
    addMeal: props.addMeal,
  };
};

export const normalizeFavMeal = (props: FavMealProps): TrackerAddMealProps => {
  // Extract all ingredients from all dishes
  const allIngredients = props.dishes.flatMap((dish) => dish.ingredients);

  // Sum ingredients with the same name
  const summedIngredients = sumIngredients(allIngredients, 1); // 1 is number of portions

  return {
    mode: 'backward-navigation',
    mealData: {
      id: props.id,
      name: props.publicName,
      kcal: props.kcal,
      macro: props.macro,
      preparationImageUrl: props.preparationImageUrl,
      mealType: props.mealType,
      sourceType: 'diet',
      ingredients: summedIngredients,
      portions: 1,
    },
    loading: props.loading,
    addMeal: props.addMeal,
  };
};

export const normalizeTrackerMeal = (props: TrackerMealProps): TrackerAddMealProps => {
  const ingredients: Ingredient[] = props.trackerMealData.products.map((product, index) => ({
    id: `product_${product.id}`,
    name: product.name,
    brand: product.brand,
    kcal: product.calories,
    measurements: product.measurement
      ? [
          {
            measureId: product.measurement.measureId,
            amount: product.measurement.amount,
            text: product.measurement.text,
            unit: product.measurement.unit,
          },
          {
            amount: product.baseAmount,
            text: `${product.baseAmount} ${product.baseUnit}`,
            unit: product.baseUnit,
          },
        ]
      : [
          {
            amount: product.baseAmount,
            text: `${product.baseAmount} ${product.baseUnit}`,
            unit: product.baseUnit,
          },
        ],
    macro: {
      protein: { grams: product.proteinGrams },
      fat: { grams: product.fatGrams },
      carbohydrates: { grams: product.carbsGrams },
    },
  }));

  return {
    mode: 'backward-navigation',
    mealData: {
      id: props.trackerMealData.id,
      name: props.trackerMealData.name,
      kcal: props.trackerMealData.calories,
      macro: {
        protein: { grams: props.trackerMealData.proteinGrams },
        fat: { grams: props.trackerMealData.fatGrams },
        carbohydrates: { grams: props.trackerMealData.carbsGrams },
      },
      preparationImageUrl: props.trackerMealData.imageUrl,
      sourceType: props.trackerMealData.sourceType as
        | 'diet'
        | 'cookbook'
        | 'ai_description'
        | 'manual',
      ingredients,
      portions: props.trackerMealData.portions,
      loggedAt: props.trackerMealData.loggedAt,
    },
    loading: props.loading,
    editMeal: props.editMeal,
    removeMeal: props.removeMeal,
    date: props.date,
  };
};

export const normalizeDescribeMeal = (props: DescribeMealProps): TrackerAddMealProps => {
  const ingredients: Ingredient[] = props.ingredients.map((ingredient, index) => ({
    id: `ingredient_${index + 1}`,
    name: ingredient.name,
    brand: ingredient.brand,
    kcal: ingredient.calories,
    measurements: [
      {
        measureId: ingredient.measurement.measureId,
        amount: ingredient.measurement.amount,
        text: ingredient.measurement.text,
        unit: ingredient.measurement.unit,
      },
      {
        amount: ingredient.baseAmount,
        text: `${ingredient.baseAmount} ${ingredient.baseUnit}`,
        unit: ingredient.baseUnit,
      },
    ],
    macro: {
      protein: { grams: ingredient.proteinGrams },
      fat: { grams: ingredient.proteinGrams },
      carbohydrates: { grams: ingredient.carbsGrams },
    },
  }));

  // Calculate total nutrition values
  const totalKcal = ingredients.reduce((sum, ing) => sum + ing.kcal, 0);
  const totalProtein = ingredients.reduce((sum, ing) => sum + (ing.macro?.protein.grams || 0), 0);
  const totalFat = ingredients.reduce((sum, ing) => sum + (ing.macro?.fat.grams || 0), 0);
  const totalCarbs = ingredients.reduce(
    (sum, ing) => sum + (ing.macro?.carbohydrates.grams || 0),
    0
  );

  return {
    mode: 'forward-navigation',
    mealData: {
      id: props.dietMealId,
      name: props.mealName,
      kcal: totalKcal,
      macro: {
        protein: { grams: totalProtein },
        fat: { grams: totalFat },
        carbohydrates: { grams: totalCarbs },
      },
      preparationImageUrl: props.imageUrl,
      sourceType: 'ai_description',
      ingredients,
      portions: props.portions,
    },
    addMeal: props.addMeal,
  };
};
